<template>
  <AppShell>
    <p v-if="isLoading" class="text-subtle">Hämtar bolån...</p>

    <template v-else-if="debt">
      <DebtHeader :debt="debt" @saved="onDebtSaved" />

      <AppTabs :is-loading="isLoading" class="debt-tabs">
        <template #left>
          <AppRouterLink
            :to="basePath"
            variant="nav"
            class="nav-link"
            exact-active-class="nav-link-active"
          >
            Bolån
          </AppRouterLink>
        </template>
        <template #right>
          <AppRouterLink
            :to="`${basePath}/dokument`"
            variant="nav"
            class="nav-link"
            active-class="nav-link-active"
          >
            Dokument
          </AppRouterLink>
        </template>
      </AppTabs>

      <router-view :debt="debt" />
    </template>
  </AppShell>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AppTabs } from '@staff/ui'

import { notify } from '@staff/ui'
import AppShell from '@/components/AppShell.vue'
import AppRouterLink from '@/components/AppRouterLink.vue'
import { fetchDebtById } from '@/services/debt'
import type { Debt } from './debt.types'
import DebtHeader from './DebtHeader.vue'

const route = useRoute()

const isLoading = ref(true)
const debt = ref<Debt | null>(null)

const debtId = computed(() => route.params.debt_id.toString())
const basePath = computed(() => `/bolan/${debtId.value}`)

const loadDebt = async () => {
  isLoading.value = true

  try {
    debt.value = await fetchDebtById(debtId.value)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Ett fel uppstod när bolånet hämtades.'
    notify({ type: 'error', title: message, dismissable: true })
  } finally {
    isLoading.value = false
  }
}

// Servicing actions (förvaltningsåtgärder) reads the updated debt after saving, so we pass the updated debt to this layout
const onDebtSaved = (updatedDebt: Debt) => {
  debt.value = updatedDebt
}

onMounted(() => {
  void loadDebt()
})

watch(debtId, () => {
  void loadDebt()
})
</script>

<style scoped lang="scss">
.debt-tabs {
  --app-tabs-bg: var(--color-canvas-inset);

  margin-top: var(--space-16);
  padding-inline: var(--space-16);
}

// Matches the tab styling in ApplicationLayout.
.nav-link:hover {
  background-color: var(--color-button-default-hover-bg);
}

.nav-link-active {
  background-color: var(--color-brand-subtle);
  color: var(--color-brand-default);
  font-weight: var(--font-weight-semibold);
}
</style>
